import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { ForgotPasswordDraft, ForgotPasswordError } from '../domain/forgot-password.model';
import { initialState } from './forgot-password.state';

/**
 * Forgot Password slice. Reducers are pure state transitions only; the submit
 * I/O (the `POST /auth/forgot-password` call) lives in the saga, which
 * dispatches these `submit*` transitions as it progresses. This is what lets the
 * reducers be unit-tested as `(state, action) => state` with zero mocks.
 */
const slice = createSlice({
  name: 'forgotPassword',
  initialState,
  reducers: {
    /** A single text field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof ForgotPasswordDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
    },
    // ── Submit lifecycle (dispatched by the saga) ──
    submitStarted: (state) => {
      state.status = 'submitting';
      state.error = null;
      state.validationErrors = {};
    },
    submitSucceeded: (state) => {
      state.status = 'succeeded';
    },
    submitFailed: (state, action: PayloadAction<ForgotPasswordError>) => {
      state.status = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** Reset to the pristine slice (on unmount). */
    resetForm: () => initialState,
  },
});

export const forgotPasswordActions = slice.actions;
export const forgotPasswordReducer = slice.reducer;
